import {passiveEventOption} from './supports-passive-events';

type MouseEventHandler = (event: MouseEvent) => void;
type TouchEventHandler = (event: TouchEvent) => void;
type SelectEventHandler = (event: Event) => void;

const body = document.body;

/******************************************************************************/

let attachedMouseDown: MouseEventHandler | null = null;

function attachMouseDown(handler: MouseEventHandler) {
  detachMouseDown();
  body.addEventListener('mousedown', handler);
  attachedMouseDown = handler;
}

function detachMouseDown() {
  if (attachedMouseDown !== null)
    body.removeEventListener('mousedown', attachedMouseDown);
  attachedMouseDown = null;
}

/******************************************************************************/

let attachedMouseMove: MouseEventHandler | null = null;

function attachMouseMove(handler: MouseEventHandler) {
  detachMouseMove();
  body.addEventListener('mousemove', handler);
  attachedMouseMove = handler;
}

function detachMouseMove() {
  if (attachedMouseMove !== null)
    body.removeEventListener('mousemove', attachedMouseMove);
  attachedMouseMove = null;
}

/******************************************************************************/

let attachedMouseUp: MouseEventHandler | null = null;

function attachMouseUp(handler: MouseEventHandler) {
  detachMouseUp();
  body.addEventListener('mouseup', handler);
  attachedMouseUp = handler;
}

function detachMouseUp() {
  if (attachedMouseUp !== null)
    body.removeEventListener('mouseup', attachedMouseUp);
  attachedMouseUp = null;
}

/******************************************************************************/

let attachedTouchStart: TouchEventHandler | null = null;

function attachTouchStart(handler: TouchEventHandler) {
  detachTouchStart();
  body.addEventListener('touchstart', handler, passiveEventOption());
  attachedTouchStart = handler;
}

function detachTouchStart() {
  if (attachedTouchStart !== null)
    body.removeEventListener('touchstart', attachedTouchStart);
  attachedTouchStart = null;
}

/******************************************************************************/

let attachedTouchMove: TouchEventHandler | null = null;

function attachTouchMove(handler: TouchEventHandler) {
  detachTouchMove();
  body.addEventListener('touchmove', handler);
  attachedTouchMove = handler;
}

function detachTouchMove() {
  if (attachedTouchMove !== null)
    body.removeEventListener('touchmove', attachedTouchMove);
  attachedTouchMove = null;
}

/******************************************************************************/

let attachedTouchEnd: TouchEventHandler | null = null;

function attachTouchEnd(handler: TouchEventHandler) {
  detachTouchEnd();
  body.addEventListener('touchend', handler);
  attachedTouchEnd = handler;
}

function detachTouchEnd() {
  if (attachedTouchEnd !== null)
    body.removeEventListener('touchmove', attachedTouchEnd);
  attachedTouchEnd = null;
}

/******************************************************************************/

let attachedSelectStartHandler: SelectEventHandler | null = null;

function attachSelectStartHandler(handler: SelectEventHandler) {
  detachSelectStartHandler();
  window.addEventListener('selectstart', handler);
  attachedSelectStartHandler = handler;
}

function detachSelectStartHandler() {
  if (attachedSelectStartHandler !== null)
    window.removeEventListener('selectstart', attachedSelectStartHandler);
  attachedSelectStartHandler = null;
}

function disableSelection(event: Event) {
  event.preventDefault();
}

/******************************************************************************/

let attachedStoreId: number;

function beginMove(handlers: {
  storeId: number;
  mouseDown: MouseEventHandler,
  mouseUp: MouseEventHandler,
  mouseMove: MouseEventHandler,
  touchStart: TouchEventHandler,
  touchMove: TouchEventHandler,
  touchEnd: TouchEventHandler,
}) {
  attachedStoreId = handlers.storeId;

  attachMouseDown(handlers.mouseDown);
  attachMouseMove(handlers.mouseMove);
  attachMouseUp(handlers.mouseUp);

  attachTouchStart(handlers.touchStart);
  attachTouchMove(handlers.touchMove);
  attachTouchEnd(handlers.touchEnd);

  attachSelectStartHandler(disableSelection);
}

function endMove(storeId: number) {
  if (storeId !== attachedStoreId)
    return;

  detachMouseDown();
  detachMouseMove();
  detachMouseUp();

  detachTouchStart();
  detachTouchMove();
  detachTouchEnd();

  detachSelectStartHandler();
}

class GlobalDragEvents {
  readonly beginMove = beginMove;
  readonly endMove = endMove;
}

export const globalDragEvents = new GlobalDragEvents();
